import { useSelector } from "react-redux";
import { useEffect, useState } from "react";
import { Dialog } from "@mui/material";
import { RootState } from "app/store";
import { useScale } from "common/utils";
import { ParentCloseBtn, FadeTransition } from "common/elements";
import i18n from "features/shared/i18n";
import { AIChatSession, AIChatMessage as AIChatMessageType } from "features/player/types";
import { getAIChatMessages } from "features/player/api";
import { AIChatMessage } from "./AIChatMessage";

interface AIChatDetailModalProps {
  session: AIChatSession;
  childIcon?: string;
  onClose: () => void;
}

export const AIChatDetailModal = ({ session, childIcon, onClose }: AIChatDetailModalProps) => {
  const { scale } = useScale();
  const parent = useSelector((state: RootState) => state.user.parent);
  const [messages, setMessages] = useState<AIChatMessageType[]>([]);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    const fetchMessages = async () => {
      if (!parent?.uid) return;

      try {
        setIsLoading(true);
        const response = await getAIChatMessages({
          parentUid: parent.uid,
          sessionId: session.id,
        });
        setMessages(response.messages);
      } catch (error) {
        console.error("Failed to fetch AI chat messages:", error);
      } finally {
        setIsLoading(false);
      }
    };

    fetchMessages();
  }, [parent?.uid, session.id]);

  return (
    <Dialog
      fullScreen
      open={true}
      maxWidth={false}
      sx={{ zIndex: 99999 }}
      TransitionComponent={FadeTransition}
      PaperProps={{ style: { backgroundColor: "transparent" } }}
    >
      <div className="flex-col-center w-full h-full bg-textcolor-black/50">
        <div style={{ transform: `scale(${scale})` }}>
          <div className="flex-col-el w-[600px] h-[700px] border-solid border-gray2-30 border-[16px] rounded-[16px] bg-gray2-30">
            <div className="flex-col-el justify-start w-full h-full relative p-6 rounded-[8px] bg-white overflow-hidden">
              <ParentCloseBtn close={onClose} />
              {/* タイトル */}
              <div className="flex-row-el items-center gap-2 mb-4 pb-3 border-b border-outline shrink-0">
                <span className="material-symbols-outlined !text-[54px] text-red-100">
                  smart_toy
                </span>
                <h2 className="text-[33px] text-textcolor-black truncate flex-1">
                  {session.title || i18n.t("MSG_PARENT_PAGE_AI_CHAT_NO_TITLE")}
                </h2>
              </div>

              {/* メッセージ表示 */}
              <div className="flex-1 overflow-y-auto invisible-scrollbar min-h-0">
                {isLoading ? (
                  <div className="flex-col-center py-8 text-gray2-100">
                    <span className="material-symbols-outlined text-[60px] mb-2 animate-spin">
                      refresh
                    </span>
                    <span className="text-[21px]">{i18n.t("MSG_LOADING")}</span>
                  </div>
                ) : messages.length > 0 ? (
                  <AIChatMessage messages={messages} childIcon={childIcon} />
                ) : (
                  <div className="flex-col-center py-8 text-gray2-100">
                    <span className="material-symbols-outlined text-[60px] mb-2">chat</span>
                    <span className="text-[21px]">
                      {i18n.t("MSG_PARENT_PAGE_AI_CHAT_NO_MESSAGES")}
                    </span>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </Dialog>
  );
};
